import ProfessionalIcon from '../../components/common/ProfessionalIcon';
import { cookieStorage } from '../../utils/cookieStorage';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.tsx';
import { useTheme } from '../../context/ThemeContext.tsx';
import BorderGlow from '../../components/common/BorderGlow.tsx';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration.tsx';
import { X, MessageCircle, Mail, Search, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { TierAvatar, getUserRingTier, getProfessionColor, UserRingTier } from '../../utils/userTier.ts';
import { axiosInstance } from '../../services/axiosConfig.ts';
import { fetchRamasProfesionales, RamaProfesional } from '../../services/ramaProfesionalService.ts';

// ─── Paleta Oficial Co Marca (con códigos exactos de diseño) ───
const colors = {
    primary:         '#c1348b', // Rosa Neón Co Marca
    primaryDark:     '#8f1f64', // Vino Oscuro
    secondary:       '#C1348B', // Variante Morado / Fucsia
    secondaryLight:  '#8b3fa8', // Turquesa
    accent:          '#ffd97c', // Amarillo
    accentDark:      '#ea8406', // Naranja
    moradoPrincipal: '#61257B', // Principal Morado
    moradoVariante:  '#C1348B', // Variante de Morado
    naranjaIntenso:  '#EA8406', // Naranja Intenso
    naranjaVariante: '#FFD97C', // Variante Naranja Intenso
};

function useInView(threshold = 0.1): [React.RefObject<HTMLElement>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) { setInView(true); return; }
        }
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

// ── Tipos ──
interface Professional {
    id: string;
    name: string;
    role: string;
    specialty: string;
    experience: string;
    available: boolean;
    avatar: string;
    initials: string;
    tags: string[];
    color: string;
    isCurrentUser?: boolean;
    phone?: string;
    email?: string;
    tier?: UserRingTier;
}

interface Category {
    id: string;
    label: string;
    icon: string;
    color: string;
    gradient: string;
    count: number;
    professionals: Professional[];
}

// ── Datos Base (Con la paleta exacta de Ishtar Villazón) ──
const rawCategories: Category[] = [
    {
        id: 'derecho',
        label: 'Derecho & Legal',
        icon: '⚖️',
        color: colors.primaryDark,
        gradient: `linear-gradient(135deg, #60002e, ${colors.primaryDark})`,
        count: 8,
        professionals: [],
    },
    {
        id: 'tecnologia',
        label: 'Tecnología & Software',
        icon: '💻',
        color: colors.moradoVariante,
        gradient: `linear-gradient(135deg, #451759, ${colors.moradoVariante})`,
        count: 14,
        professionals: [],
    },
    {
        id: 'salud',
        label: 'Salud & Bienestar',
        icon: '🧠',
        color: colors.secondaryLight,
        gradient: `linear-gradient(135deg, #007a68, ${colors.secondaryLight})`,
        count: 6,
        professionals: [],
    },
    {
        id: 'finanzas',
        label: 'Finanzas & Contabilidad',
        icon: '📊',
        color: colors.naranjaIntenso,
        gradient: `linear-gradient(135deg, #b35900, ${colors.naranjaIntenso})`,
        count: 10,
        professionals: [],
    },
    {
        id: 'marketing',
        label: 'Marketing & Comunicación',
        icon: '📣',
        color: colors.primary,
        gradient: `linear-gradient(135deg, ${colors.primaryDark}, ${colors.primary})`,
        count: 9,
        professionals: [],
    },
    {
        id: 'ingenieria',
        label: 'Ingeniería & Arquitectura',
        icon: '🏗️',
        color: colors.moradoPrincipal,
        gradient: `linear-gradient(135deg, ${colors.moradoPrincipal}, ${colors.moradoVariante})`,
        count: 7,
        professionals: [],
    },
];

// ── Professional Card ──
function ProfessionalCard({ 
    pro, 
    index, 
    onContact 
}: { 
    pro: Professional; 
    index: number; 
    onContact: (pro: Professional) => void;
}) {
    const { user } = useAuth();
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.05);
    const [hovered, setHovered] = useState(false);

    const proTier = pro.isCurrentUser ? getUserRingTier(user) : (pro.tier || 'comun');

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                flex: '0 0 260px',
                width: '260px',
                minWidth: '260px',
                maxWidth: '260px',
                scrollSnapAlign: 'start',
                transform: inView ? (hovered ? 'translateY(-6px)' : 'translateY(0)') : 'translateY(24px)',
                opacity: inView ? 1 : 0,
                transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.05}s`,
            }}
        >
            <BorderGlow
                borderRadius={22}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
            >
                <div
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    style={{
                        background: isDark ? '#150d24' : '#fff',
                        borderRadius: '22px',
                        border: pro.isCurrentUser ? `2px solid ${pro.color}` : (isDark ? '1px solid rgba(193,52,139,0.25)' : (hovered ? `1.5px solid ${pro.color}40` : '1.5px solid #f1f5f9')),
                        boxShadow: pro.isCurrentUser
                            ? `0 12px 36px ${pro.color}25, 0 2px 10px rgba(0,0,0,0.04)`
                            : (hovered ? `0 16px 48px ${pro.color}18, 0 4px 16px rgba(0,0,0,0.06)` : '0 2px 12px rgba(0,0,0,0.04)'),
                        padding: '24px 20px',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        gap: '12px', textAlign: 'center',
                        transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
                        cursor: 'default', position: 'relative',
                        width: '100%',
                    }}
                >


                    {/* Disponibilidad o Badge de Usuario Actual en esquina superior derecha */}
                    <div style={{
                        position: 'absolute', top: '14px', right: '14px',
                        display: 'flex', alignItems: 'center', gap: '5px',
                        background: pro.isCurrentUser ? `${pro.color}18` : (pro.available ? (isDark ? 'rgba(139, 63, 168,0.15)' : `${colors.secondaryLight}18`) : (isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9')),
                        borderRadius: '100px', padding: '3px 10px',
                        fontSize: '0.68rem', fontWeight: '800',
                        color: pro.isCurrentUser ? pro.color : (pro.available ? (isDark ? '#8b3fa8' : '#006b5a') : '#94a3b8'),
                        border: pro.isCurrentUser ? `1.5px solid ${pro.color}40` : (pro.available ? `1px solid ${colors.secondaryLight}40` : (isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0')),
                    }}>
                        <span style={{
                            width: '6px', height: '6px', borderRadius: '50%',
                            background: pro.isCurrentUser ? pro.color : (pro.available ? colors.secondaryLight : '#cbd5e1'),
                            display: 'inline-block',
                        }} />
                        {pro.isCurrentUser ? 'Tú (Activo)' : (pro.available ? 'Disponible' : 'Ocupado')}
                    </div>

                    {/* Avatar con aro según el tier del profesional */}
                    <div style={{ position: 'relative', width: '74px', height: '74px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <TierAvatar
                            tier={proTier}
                            size={72}
                            ringThickness={3}
                            initial={pro.initials}
                            bgColor={pro.color}
                            fotoUrl={pro.avatar}
                            isOpenOrHovered={hovered || !!pro.isCurrentUser}
                        />
                    </div>

                    {/* Info */}
                    <div style={{ minWidth: 0, width: '100%' }}>
                        <div style={{ fontWeight: '800', fontSize: '0.98rem', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '3px', lineHeight: '1.2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {pro.name}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: isDark ? '#FFD97C' : pro.color, fontWeight: '700', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {pro.role}
                        </div>
                        <div style={{ fontSize: '0.74rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500' }}>
                            {pro.experience}
                        </div>
                    </div>

                    {/* Tags */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
                        {pro.tags.slice(0, 2).map(tag => (
                            <span key={tag} style={{
                                background: isDark ? 'rgba(255,255,255,0.06)' : `${pro.color}10`,
                                border: isDark ? '1px solid rgba(255,255,255,0.12)' : `1px solid ${pro.color}20`,
                                borderRadius: '6px', padding: '3px 9px',
                                fontSize: '0.68rem', fontWeight: '600', color: isDark ? '#FFD97C' : pro.color,
                            }}>
                                {tag}
                            </span>
                        ))}
                    </div>

                    {/* CTA */}
                    <button
                        type="button"
                        onClick={() => onContact(pro)}
                        disabled={!pro.available}
                        style={{
                            width: '100%', padding: '10px 0', borderRadius: '12px', border: 'none',
                            background: pro.available
                                ? `linear-gradient(135deg, ${pro.color}, ${pro.color}ee)`
                                : (isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'),
                            color: pro.available ? '#fff' : '#94a3b8',
                            fontWeight: '700', fontSize: '0.82rem', cursor: pro.available ? 'pointer' : 'not-allowed',
                            fontFamily: "'DM Sans', sans-serif",
                            transition: 'all 0.22s ease',
                            boxShadow: pro.available ? `0 4px 14px ${pro.color}35` : 'none',
                            transform: hovered && pro.available ? 'scale(1.02)' : 'scale(1)',
                            marginTop: 'auto',
                        }}
                    >
                        {pro.isCurrentUser ? 'Ver mi contacto →' : (pro.available ? 'Contactar →' : 'No disponible')}
                    </button>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Modal de Contacto ──
function ContactModal({ 
    pro, 
    onClose 
}: { 
    pro: Professional | null; 
    onClose: () => void;
}) {
    const { isDark } = useTheme();
    if (!pro) return null;

    const whatsappMessage = encodeURIComponent(`Hola ${pro.name}, vi tu perfil en el Banco de Profesionales de Co Marca y me gustaría ponerme en contacto contigo para una consulta.`);
    const phone = pro.phone ? pro.phone.replace(/[^0-9]/g, '') : '59170000000';
    const whatsappUrl = `https://wa.me/${phone}?text=${whatsappMessage}`;
    const mailUrl = `mailto:${pro.email || 'contacto@comarca.bo'}?subject=${encodeURIComponent(`Contacto desde Co Marca - ${pro.name}`)}`;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
        }} onClick={onClose}>
            <div style={{
                background: isDark ? '#150d24' : '#fff', borderRadius: '24px', maxWidth: '440px', width: '100%',
                boxShadow: '0 25px 60px rgba(0,0,0,0.4)', overflow: 'hidden',
                border: isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #f1f5f9',
                animation: 'fadeUpModal .28s cubic-bezier(.16,1,.3,1) both',
            }} onClick={e => e.stopPropagation()}>
                {/* Header modal con color de profesión */}
                <div style={{
                    background: `linear-gradient(135deg, ${pro.color}, ${colors.primaryDark})`,
                    padding: '24px', position: 'relative', color: '#fff',
                }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            position: 'absolute', top: '16px', right: '16px',
                            background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
                            width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', cursor: 'pointer',
                        }}
                    >
                        <X size={18} />
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{
                            width: '56px', height: '56px', borderRadius: '50%',
                            background: '#fff', padding: '2px', flexShrink: 0,
                        }}>
                            <div style={{
                                width: '100%', height: '100%', borderRadius: '50%',
                                background: pro.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', fontWeight: '900', fontSize: '1.3rem',
                            }}>
                                {pro.initials}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '1.15rem', fontWeight: '800', lineHeight: 1.2 }}>{pro.name}</div>
                            <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '2px' }}>{pro.role}</div>
                            <div style={{ fontSize: '0.72rem', background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '100px', display: 'inline-block', marginTop: '6px' }}>
                                {pro.specialty}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body modal */}
                <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <p style={{ fontSize: '0.86rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.6', textAlign: 'justify' }}>
                        Selecciona el canal por el cual deseas contactar a este profesional de la red Co Marca:
                    </p>

                    <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '14px 18px', borderRadius: '14px',
                            background: '#25D36615', border: '1.5px solid #25D36640',
                            color: '#075E54', textDecoration: 'none', fontWeight: '700', fontSize: '0.92rem',
                            transition: 'all .2s ease',
                        }}
                    >
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#25D366', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MessageCircle size={20} />
                        </div>
                        <div>
                            <div>Contactar por WhatsApp</div>
                            <div style={{ fontSize: '0.72rem', color: '#128C7E', fontWeight: '500' }}>Respuesta ágil y directa</div>
                        </div>
                    </a>

                    <a
                        href={mailUrl}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '14px 18px', borderRadius: '14px',
                            background: isDark ? 'rgba(193,52,139,0.18)' : `${colors.secondary}12`,
                            border: isDark ? '1.5px solid rgba(193,52,139,0.4)' : `1.5px solid ${colors.secondary}35`,
                            color: isDark ? '#FFD97C' : colors.secondary, textDecoration: 'none', fontWeight: '700', fontSize: '0.92rem',
                            transition: 'all .2s ease',
                        }}
                    >
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: colors.secondary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Mail size={18} />
                        </div>
                        <div>
                            <div>Enviar Correo Formal</div>
                            <div style={{ fontSize: '0.72rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '500' }}>Para propuestas comerciales o proyectos</div>
                        </div>
                    </a>

                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            padding: '12px 0', borderRadius: '12px',
                            border: isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #e2e8f0',
                            background: isDark ? '#1a102a' : '#f8fafc',
                            color: isDark ? '#cbd5e1' : '#64748b',
                            fontWeight: '700', fontSize: '0.85rem',
                            cursor: 'pointer', marginTop: '6px',
                        }}
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Carrusel Elegante para Categoría de Profesionales ──
interface CategoryCarouselProps {
    children: React.ReactNode;
    color: string;
    count: number;
}

function CategoryCarousel({ children, color, count }: CategoryCarouselProps) {
    const { isDark } = useTheme();
    const trackRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScroll = () => {
        if (!trackRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = trackRef.current;
        setCanScrollLeft(scrollLeft > 10);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    };

    useEffect(() => {
        // Small delay to allow layout calculation
        const timer = setTimeout(checkScroll, 100);
        const el = trackRef.current;
        if (!el) return;
        el.addEventListener('scroll', checkScroll, { passive: true });
        window.addEventListener('resize', checkScroll);
        return () => {
            clearTimeout(timer);
            el.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, [count]);

    const handleScroll = (direction: 'left' | 'right') => {
        if (!trackRef.current) return;
        const offset = 320;
        trackRef.current.scrollBy({
            left: direction === 'left' ? -offset : offset,
            behavior: 'smooth',
        });
    };

    return (
        <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
            {/* Sombra difuminada izquierda */}
            {canScrollLeft && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: '45px',
                    background: isDark
                        ? 'linear-gradient(to right, rgba(14, 7, 25, 0.95), transparent)'
                        : 'linear-gradient(to right, rgba(248, 250, 252, 0.95), transparent)',
                    zIndex: 5,
                    pointerEvents: 'none',
                    transition: 'opacity 0.25s ease',
                }} />
            )}

            {/* Sombra difuminada derecha */}
            {canScrollRight && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    right: 0,
                    width: '45px',
                    background: isDark
                        ? 'linear-gradient(to left, rgba(14, 7, 25, 0.95), transparent)'
                        : 'linear-gradient(to left, rgba(248, 250, 252, 0.95), transparent)',
                    zIndex: 5,
                    pointerEvents: 'none',
                    transition: 'opacity 0.25s ease',
                }} />
            )}

            {/* Botón flotante deslizar izquierda */}
            {canScrollLeft && (
                <button
                    type="button"
                    onClick={() => handleScroll('left')}
                    aria-label="Anterior profesional"
                    style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: isDark ? '#1a102a' : '#ffffff',
                        border: isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #e2e8f0',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: isDark ? '#f8fafc' : '#334155',
                        transition: 'all 0.2s cubic-bezier(.16,1,.3,1)',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1.12)';
                        e.currentTarget.style.borderColor = color;
                        e.currentTarget.style.color = color;
                        e.currentTarget.style.boxShadow = `0 6px 20px ${color}35`;
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        e.currentTarget.style.borderColor = isDark ? 'rgba(193,52,139,0.3)' : '#e2e8f0';
                        e.currentTarget.style.color = isDark ? '#f8fafc' : '#334155';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                    }}
                >
                    <ChevronLeft size={20} />
                </button>
            )}

            {/* Botón flotante deslizar derecha */}
            {canScrollRight && (
                <button
                    type="button"
                    onClick={() => handleScroll('right')}
                    aria-label="Siguiente profesional"
                    style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: isDark ? '#1a102a' : '#ffffff',
                        border: isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #e2e8f0',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: isDark ? '#f8fafc' : '#334155',
                        transition: 'all 0.2s cubic-bezier(.16,1,.3,1)',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1.12)';
                        e.currentTarget.style.borderColor = color;
                        e.currentTarget.style.color = color;
                        e.currentTarget.style.boxShadow = `0 6px 20px ${color}35`;
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        e.currentTarget.style.borderColor = isDark ? 'rgba(193,52,139,0.3)' : '#e2e8f0';
                        e.currentTarget.style.color = isDark ? '#f8fafc' : '#334155';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                    }}
                >
                    <ChevronRight size={20} />
                </button>
            )}

            {/* Pista deslizable con scroll snap */}
            <div
                ref={trackRef}
                className="banco-carousel-track"
                style={{
                    display: 'flex',
                    gap: '18px',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    padding: '24px 28px',
                    scrollBehavior: 'smooth',
                    background: isDark ? '#0e0719' : '#f8fafc',
                }}
            >
                {children}
            </div>
        </div>
    );
}

// ── Usuarios Reales de la Base de Datos Co Marca ──


// ── Main Component ──
const ComunidadBanco: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProForContact, setSelectedProForContact] = useState<Professional | null>(null);
    const [, setStorageTick] = useState(0);
    const [backendUsers, setBackendUsers] = useState<any[]>([]);
    const [, setLoadingUsers] = useState<boolean>(false);
    const [dynamicRamas, setDynamicRamas] = useState<RamaProfesional[]>([]);

    useEffect(() => {
        let isMounted = true;
        fetchRamasProfesionales().then(res => {
            if (isMounted && res && res.length > 0) {
                setDynamicRamas(res);
            }
        }).catch(err => console.error('Error fetching ramas in Banco:', err));
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        let isMounted = true;
        setLoadingUsers(true);
        axiosInstance.get('banco-profesionales/')
            .then(res => {
                if (isMounted && Array.isArray(res.data)) {
                    setBackendUsers(res.data);
                }
            })
            .catch(err => {
                console.error('Error cargando banco de profesionales:', err);
            })
            .finally(() => {
                if (isMounted) setLoadingUsers(false);
            });
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        const onStorage = () => setStorageTick(t => t + 1);
        window.addEventListener('cookie-preferences-changed', onStorage);
        return () => window.removeEventListener('cookie-preferences-changed', onStorage);
    }, []);

    const baseCategories = useMemo(() => {
        if (!dynamicRamas || dynamicRamas.length === 0) return rawCategories;
        const updated = rawCategories.map(cat => {
            const found = dynamicRamas.find(r => r.codigo === cat.id);
            if (found) {
                return {
                    ...cat,
                    label: found.nombre || cat.label,
                    icon: found.icono || cat.icon,
                    color: found.color || cat.color,
                    gradient: `linear-gradient(135deg, ${found.color_dark || cat.color}, ${found.color || cat.color})`,
                };
            }
            return cat;
        });

        dynamicRamas.forEach(r => {
            if (r.activo && !updated.some(c => c.id === r.codigo)) {
                updated.push({
                    id: r.codigo,
                    label: r.nombre,
                    icon: r.icono || '💼',
                    color: r.color || '#61257B',
                    gradient: `linear-gradient(135deg, ${r.color_dark || '#61257B'}, ${r.color || '#C1348B'})`,
                    count: 0,
                    professionals: [],
                });
            }
        });
        return updated;
    }, [dynamicRamas]);

    // Integración de usuarios reales de la base de datos en las categorías de profesionales
    const currentSavedProf = cookieStorage.getItem('user_profesion') || user?.especialidad || user?.cargo || '';
    const currentSavedCat = cookieStorage.getItem('user_categoria') || '';
    const categories = useMemo(() => {

        return baseCategories.map((cat) => {
            // 1. Filtrar los usuarios reales del backend pertenecientes a esta categoría
            const matchedBackendUsers = backendUsers.filter(bu => {
                const prof = getProfessionColor(bu);
                return prof.categoria === cat.id;
            });

            const realPros: Professional[] = matchedBackendUsers.map(bu => {
                const isCurrent = user && (user.id === bu.id || user.email === bu.email);
                const profInfo = getProfessionColor(bu);
                const firstName = bu.first_name || '';
                const lastName = bu.last_name || '';
                const fullName = `${firstName} ${lastName}`.trim() || bu.email;
                const initials = firstName ? firstName[0].toUpperCase() : (bu.email ? bu.email[0].toUpperCase() : 'C');

                let role = bu.cargo || profInfo.label || 'Especialista Co Marca';
                if (isCurrent && currentSavedProf) {
                    role = currentSavedProf;
                }

                const specialty = bu.empresa ? `${cat.label} · ${bu.empresa}` : cat.label;
                const experience = bu.tier === 'superadmin'
                    ? 'Fundador / Dirección'
                    : (bu.tier === 'personal'
                        ? 'Equipo Co Marca'
                        : (bu.tier === 'domiciliado'
                            ? 'Afiliado Verificado'
                            : 'Miembro Activo'));

                const tags = [
                    bu.role || 'Miembro',
                    bu.empresa ? 'Empresa Registrada' : 'Verificado',
                ];

                return {
                    id: `db-user-${bu.id}`,
                    name: fullName,
                    role,
                    specialty,
                    experience,
                    available: true,
                    avatar: bu.foto_url || '',
                    initials,
                    tags,
                    color: cat.color,
                    isCurrentUser: !!isCurrent,
                    phone: bu.telefono || '',
                    email: bu.email,
                    tier: (isCurrent ? getUserRingTier(user) : (bu.tier || 'comun')) as UserRingTier,
                };
            });

            // 2. Si el usuario actual está logueado y no está en realPros
            if (user && !realPros.some(rp => rp.isCurrentUser)) {
                const userProfInfo = getProfessionColor(user);
                const userCat = currentSavedCat || userProfInfo.categoria;
                if (cat.id === userCat) {
                    const userPro: Professional = {
                        id: `user-${user.id}`,
                        name: `${user.first_name} ${user.last_name}`.trim() || user.email,
                        role: currentSavedProf || user.cargo || userProfInfo.label || 'Especialista Co Marca',
                        specialty: `${cat.label}`,
                        experience: 'Miembro Activo',
                        available: true,
                        avatar: user.foto_url || '',
                        initials: user.first_name ? user.first_name[0].toUpperCase() : 'C',
                        tags: ['Miembro Co Marca', 'Verificado'],
                        color: cat.color,
                        isCurrentUser: true,
                        phone: user.telefono || '',
                        email: user.email,
                        tier: getUserRingTier(user),
                    };
                    realPros.unshift(userPro);
                }
            }

            let pros = realPros;

            // 4. Filtrado por búsqueda
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                pros = pros.filter(p =>
                    p.name.toLowerCase().includes(q) ||
                    p.role.toLowerCase().includes(q) ||
                    p.specialty.toLowerCase().includes(q) ||
                    p.tags.some(t => t.toLowerCase().includes(q))
                );
            }

            return {
                ...cat,
                count: pros.length,
                professionals: pros,
            };
        });
    }, [user, backendUsers, searchQuery, currentSavedProf, currentSavedCat, baseCategories]);

    const { isDark } = useTheme();
    const hasResults = categories.some(c => c.professionals.length > 0);

    return (
        <div style={{
            fontFamily: "'DM Sans', sans-serif",
            background: isDark ? '#0b0614' : '#f8fafc',
            minHeight: '100vh',
            transition: 'background 0.3s ease',
        }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
                * { box-sizing: border-box; margin: 0; padding: 0; }
                @keyframes fadeUpModal { from { opacity:0; transform:scale(.95) translateY(12px); } to { opacity:1; transform:scale(1) translateY(0); } }
                @keyframes pulseDot { 0%, 100% { box-shadow: 0 0 0 0 rgba(193,52,139,0.4); } 50% { box-shadow: 0 0 0 5px rgba(193,52,139,0); } }
                .banco-search-input:focus {
                    border-color: #C1348B !important;
                    box-shadow: 0 0 0 4px rgba(193, 52, 139, 0.15), 0 4px 20px rgba(0, 0, 0, 0.06) !important;
                }
                .banco-carousel-track {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .banco-carousel-track::-webkit-scrollbar {
                    display: none;
                }
            `}</style>

            {/* Hero Section */}
            <section style={{
                background: isDark
                    ? 'linear-gradient(135deg, #0b0614 0%, #150a26 50%, #200d36 100%)'
                    : 'linear-gradient(135deg, #fff 0%, #faf5fc 55%, #f5e8fa 100%)',
                padding: 'clamp(90px, 12vw, 130px) 32px clamp(50px, 6vw, 70px)',
                position: 'relative', overflow: 'hidden', textAlign: 'center',
            }}>
                {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
                <HexagonCornerDecoration
                    variant="horizontal-degrade"
                    direction="left-to-right"
                    opacity={isDark ? 0.70 : 0.50}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />
                <div style={{
                    position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px',
                    background: isDark
                        ? 'radial-gradient(circle, rgba(193,52,139,0.15) 0%, rgba(97,37,123,0.05) 70%, transparent 100%)'
                        : 'radial-gradient(circle, rgba(97,37,123,0.08) 0%, rgba(193,52,139,0.02) 70%, transparent 100%)',
                    borderRadius: '50%', pointerEvents: 'none',
                }} />

                <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative' }}>
                    <h1 style={{
                        fontSize: 'clamp(2.4rem, 5vw, 3.8rem)', fontWeight: '900',
                        color: isDark ? '#ffffff' : '#0f172a',
                        lineHeight: '1.08', letterSpacing: '-0.025em', marginBottom: '12px',
                    }}>
                        Banco de <span style={{ color: isDark ? '#FFD97C' : '#61257B' }}>Profesionales</span>
                    </h1>
                    <h2 style={{
                        fontSize: 'clamp(1.1rem, 2vw, 1.35rem)', fontWeight: '600',
                        color: isDark ? '#cbd5e1' : '#475569',
                        letterSpacing: '-0.01em', marginBottom: '20px',
                    }}>
                        Talento boliviano <span style={{ color: isDark ? '#FFD97C' : '#61257B' }}>verificado y listo para colaborar</span>
                    </h2>

                    <p style={{
                        fontSize: '1.02rem', color: isDark ? '#94a3b8' : '#64748b',
                        maxWidth: '580px', margin: '0 auto 28px', lineHeight: '1.7', textAlign: 'justify',
                    }}>
                        Conectamos empresas y emprendedores con los mejores profesionales independientes de Bolivia.
                    </p>

                    {/* Búsqueda en vivo */}
                    <div style={{ maxWidth: '480px', margin: '0 auto', position: 'relative' }}>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Buscar profesional, especialidad o habilidad..."
                            className="banco-search-input"
                            style={{
                                width: '100%', padding: '14px 48px 14px 20px',
                                border: isDark ? '2px solid rgba(193,52,139,0.35)' : '2px solid #e2e8f0',
                                borderRadius: '14px',
                                fontSize: '0.93rem', fontFamily: "'DM Sans', sans-serif",
                                color: isDark ? '#f8fafc' : '#374151',
                                outline: 'none',
                                background: isDark ? '#190e2b' : '#fff',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                                transition: 'all 0.2s ease',
                            }}
                        />
                        <Search size={18} color="#94a3b8" style={{ position: 'absolute', right: '18px', top: '50%', transform: 'translateY(-50%)' }} />
                    </div>

                    {/* Barra de llamada a la acción para usuario autenticado o nuevo */}
                    <div style={{
                        marginTop: '32px',
                        background: isDark ? '#150d24' : '#fff',
                        border: isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #ebdcf0',
                        borderRadius: '16px',
                        padding: '14px 22px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '14px',
                        boxShadow: '0 4px 16px rgba(97,37,123,0.06)',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                    }}>
                        <div style={{
                            width: '36px', height: '36px', borderRadius: '50%',
                            background: 'conic-gradient(from 180deg, #61257B 0%, #C1348B 16%, #c1348b 32%, #EA8406 48%, #FFD97C 64%, #8b3fa8 80%, #C1348B 92%, #61257B 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px',
                        }}>
                            <div style={{
                                width: '100%', height: '100%', borderRadius: '50%',
                                background: isDark ? '#150d24' : '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Sparkles size={16} color={isDark ? '#FFD97C' : '#61257B'} />
                            </div>
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a' }}>
                                {user ? `Tu perfil: ${user.first_name} ${user.last_name}` : '¿Eres profesional independiente o empresa?'}
                            </div>
                            <div style={{ fontSize: '0.74rem', color: isDark ? '#cbd5e1' : '#64748b' }}>
                                {user ? 'Define tu especialidad para destacar con tu estela multicolor en el Banco.' : 'Únete a la red y recibe oportunidades de colaboración directa.'}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate(user ? '/perfil' : '/register')}
                            style={{
                                background: `linear-gradient(135deg, ${colors.moradoPrincipal}, ${colors.moradoVariante})`,
                                color: '#fff', border: 'none', borderRadius: '10px',
                                padding: '8px 18px', fontSize: '0.82rem', fontWeight: '700',
                                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                boxShadow: `0 4px 12px ${colors.moradoPrincipal}35`,
                            }}
                        >
                            {user ? 'Configurar mi profesión →' : 'Registrarme →'}
                        </button>
                    </div>
                </div>
            </section>

            {/* Categorías y Profesionales */}
            <section style={{ padding: '50px 32px 100px', maxWidth: '1200px', margin: '0 auto' }}>
                {!hasResults ? (
                    <div style={{
                        textAlign: 'center', padding: '60px 20px',
                        background: isDark ? '#150d24' : '#fff',
                        borderRadius: '24px',
                        border: isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #f1f5f9',
                    }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}><Search size={36} aria-hidden="true"/></div>
                        <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a' }}>No se encontraron profesionales</h3>
                        <p style={{ fontSize: '0.88rem', color: isDark ? '#cbd5e1' : '#64748b', marginTop: '4px' }}>Intenta buscar con otra especialidad, nombre o palabra clave.</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {categories.map((cat, i) => {
                            if (cat.professionals.length === 0) return null;
                            return (
                                <div key={cat.id} style={{
                                    background: isDark ? '#150d24' : '#fff',
                                    borderRadius: '24px',
                                    border: isDark ? '1.5px solid rgba(193,52,139,0.25)' : '1.5px solid #f1f5f9',
                                    overflow: 'hidden',
                                    boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.2)' : '0 2px 16px rgba(0,0,0,0.03)',
                                }}>
                                    {/* Cabecera de Categoría */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '20px 28px',
                                        borderBottom: isDark ? '1.5px solid rgba(193,52,139,0.18)' : '1.5px solid #f8fafc',
                                        flexWrap: 'wrap', gap: '12px',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                            <div style={{
                                                width: '46px', height: '46px', borderRadius: '14px',
                                                background: cat.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '1.35rem', boxShadow: `0 4px 14px ${cat.color}25`,
                                            }}>
                                                <ProfessionalIcon category={cat.id} size={26}/>
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '800', fontSize: '1.05rem', color: isDark ? '#ffffff' : '#0f172a' }}>
                                                    {cat.label}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500' }}>
                                                    {cat.professionals.length} profesionales registrados
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            {cat.professionals.length > 3 && (
                                                <span style={{ fontSize: '0.72rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500' }}>
                                                    Desliza para explorar →
                                                </span>
                                            )}
                                            <span style={{
                                                background: `${cat.color}14`, border: `1.5px solid ${cat.color}25`,
                                                borderRadius: '100px', padding: '4px 14px',
                                                fontSize: '0.75rem', fontWeight: '700', color: isDark ? '#FFD97C' : cat.color,
                                            }}>
                                                {cat.professionals.length} disponibles
                                            </span>
                                        </div>
                                    </div>

                                    {/* Carrusel Deslizable de Profesionales con Efecto Suave */}
                                    <CategoryCarousel color={cat.color} count={cat.professionals.length}>
                                        {cat.professionals.map((pro, idx) => (
                                            <ProfessionalCard
                                                key={pro.id}
                                                pro={pro}
                                                index={idx}
                                                onContact={setSelectedProForContact}
                                            />
                                        ))}
                                    </CategoryCarousel>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* Modal Interactivo de Contacto */}
            <ContactModal
                pro={selectedProForContact}
                onClose={() => setSelectedProForContact(null)}
            />
        </div>
    );
};

export default ComunidadBanco;
